import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import apiService from '../services/apiService';

// Log de viabilidade de todas as vendas recebidas (gravado pelo cron a cada 10 min):
// margem praticada de cada item e a classificação pela política de preços.

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CLASSIFICACOES = {
  viavel:     { label: 'Viável',            cls: 'bg-green-100 text-green-800' },
  gerente:    { label: 'Liberação gerente', cls: 'bg-amber-100 text-amber-800' },
  inviavel:   { label: 'Inviável',          cls: 'bg-red-100 text-red-700' },
  impossivel: { label: 'Impossível',        cls: 'bg-red-800 text-white' },
};

function ClassificacaoBadge({ classificacao }) {
  const c = CLASSIFICACOES[classificacao] ?? { label: classificacao, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded whitespace-nowrap ${c.cls}`}>
      {c.label}
    </span>
  );
}

function fmtDataHora(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

const fmtMoeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = (v) => `${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

// ─── Componente principal ─────────────────────────────────────────────────────

export default function MargensLucroJuros() {
  const navigate = useNavigate();

  // Filtros
  const [filtroInicio,        setFiltroInicio]        = useState('');
  const [filtroFim,           setFiltroFim]           = useState('');
  const [filtroClassificacao, setFiltroClassificacao] = useState('');

  // Dados
  const [registros,  setRegistros]  = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
  const [loading,    setLoading]    = useState(false);
  const [erro,       setErro]       = useState('');

  // ── Buscar ─────────────────────────────────────────────────────────────────
  const buscar = useCallback(async (page = 1, filtros = {}) => {
    const { inicio = filtroInicio, fim = filtroFim, classificacao = filtroClassificacao } = filtros;
    setLoading(true);
    setErro('');
    try {
      const resp = await apiService.margensLucroJuros.listar({
        page,
        limit: 20,
        dataInicio:    inicio        || undefined,
        dataFim:       fim           || undefined,
        classificacao: classificacao || undefined,
      });
      const d = resp.data;
      setRegistros(d.data ?? []);
      setPagination(d.pagination ?? { total: 0, page: 1, limit: 20, pages: 1 });
    } catch {
      setErro('Erro ao carregar as margens. Verifique a conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  }, [filtroInicio, filtroFim, filtroClassificacao]);

  // Carrega ao montar
  useEffect(() => { buscar(1); }, []); // eslint-disable-line

  function handleBuscar(e) {
    e.preventDefault();
    buscar(1);
  }

  function limpar() {
    setFiltroInicio('');
    setFiltroFim('');
    setFiltroClassificacao('');
    buscar(1, { inicio: '', fim: '', classificacao: '' });
  }

  function irPagina(p) {
    if (p < 1 || p > pagination.pages) return;
    buscar(p);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} />Voltar
        </button>
        <div>
          <h1 className="text-xl font-medium text-gray-900 leading-tight">Margens Lucro/Juros</h1>
          <p className="text-sm text-gray-500">
            Margem praticada em cada item vendido (equivalente à vista, já descontados juros e inadimplência)
          </p>
        </div>
      </div>

      {/* Filtros */}
      <form onSubmit={handleBuscar} className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[160px]">
            <label className="text-xs text-gray-500 block mb-1">De</label>
            <input
              type="date"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              value={filtroInicio}
              onChange={e => setFiltroInicio(e.target.value)}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="text-xs text-gray-500 block mb-1">Até</label>
            <input
              type="date"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              value={filtroFim}
              onChange={e => setFiltroFim(e.target.value)}
            />
          </div>

          <div className="min-w-[200px]">
            <label className="text-xs text-gray-500 block mb-1">Classificação</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              value={filtroClassificacao}
              onChange={e => setFiltroClassificacao(e.target.value)}
            >
              <option value="">Todas</option>
              {Object.entries(CLASSIFICACOES).map(([valor, { label }]) => (
                <option key={valor} value={valor}>{label}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={limpar}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50 flex items-center gap-1.5"
            >
              <RefreshCw size={13} />Limpar
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100"
            >
              <Search size={14} />Buscar
            </button>
          </div>
        </div>
      </form>

      {/* Tabela */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

        {/* Totalizador */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-500">
            {loading ? 'Carregando...' : `${pagination.total} registro(s) encontrado(s)`}
          </span>
          {pagination.pages > 1 && (
            <span className="text-xs text-gray-400">
              Página {pagination.page} de {pagination.pages}
            </span>
          )}
        </div>

        {erro && (
          <div className="px-5 py-8 text-center text-sm text-red-600">{erro}</div>
        )}

        {!erro && !loading && registros.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">
            Nenhuma venda encontrada com os filtros informados.
          </div>
        )}

        {!erro && registros.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100">
                  <th className="text-left px-5 py-3 font-medium">Data</th>
                  <th className="text-left px-4 py-3 font-medium">Produto</th>
                  <th className="text-right px-4 py-3 font-medium">Custo médio</th>
                  <th className="text-right px-4 py-3 font-medium">Vendido</th>
                  <th className="text-left px-4 py-3 font-medium">Pagamento</th>
                  <th className="text-right px-4 py-3 font-medium">Parcelas</th>
                  <th className="text-right px-4 py-3 font-medium">Margem</th>
                  <th className="text-left px-4 py-3 font-medium">Classificação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {registros.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">

                    {/* Data / venda */}
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="text-gray-900">{fmtDataHora(r.data_venda)}</div>
                      <div className="text-xs text-gray-400">
                        Venda {r.sequencia}{r.vendedor ? ` · ${r.vendedor}` : ''}
                      </div>
                    </td>

                    {/* Produto */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[260px]" title={r.descricao_produto}>
                        {r.descricao_produto || '—'}
                      </div>
                      <div className="text-xs text-gray-400">Cód. {r.codigo_produto}</div>
                    </td>

                    <td className="px-4 py-3 text-right whitespace-nowrap text-gray-700">{fmtMoeda(r.custo_medio)}</td>

                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="text-gray-900">{fmtMoeda(r.valor_vendido)}</div>
                      {Number(r.preco_sugerido) > 0 && (
                        <div className="text-xs text-gray-400">Sugerido {fmtMoeda(r.preco_sugerido)}</div>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600 truncate max-w-[180px] block" title={r.forma_pagamento}>
                        {r.forma_pagamento || '—'}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right text-gray-700">
                      {r.parcelas > 0 ? `${r.parcelas}x` : 'À vista'}
                    </td>

                    <td className="px-4 py-3 text-right whitespace-nowrap font-medium text-gray-900">
                      {fmtPct(r.margem_praticada)}
                    </td>

                    <td className="px-4 py-3">
                      <ClassificacaoBadge classificacao={r.classificacao} />
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginação */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <button
              onClick={() => irPagina(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />Anterior
            </button>

            <div className="flex gap-1">
              {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
                let p;
                if (pagination.pages <= 7) {
                  p = i + 1;
                } else if (pagination.page <= 4) {
                  p = i + 1;
                } else if (pagination.page >= pagination.pages - 3) {
                  p = pagination.pages - 6 + i;
                } else {
                  p = pagination.page - 3 + i;
                }
                return (
                  <button
                    key={p}
                    onClick={() => irPagina(p)}
                    disabled={loading}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors
                      ${p === pagination.page
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => irPagina(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Próxima<ChevronRight size={14} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
